import { useEffect, useRef, useState } from "react";

import { useAlert } from "pages/context/AlertContext";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "pages/components/PageHeader";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

export default function TesteBarcodeDetector() {
  const { showAlert } = useAlert();

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [formato, setFormato] = useState("");
  const [suportaDetector, setSuportaDetector] = useState(false);

  const pararCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch {}
  };

  const iniciarCamera = async () => {
    setLoading(true);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        showAlert("Este browser não permite acesso à câmara neste contexto.", "warning");
        setLoading(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error(err);
      showAlert("Não foi possível iniciar a câmara.", "error");
    } finally {
      setLoading(false);
    }
  };

  const capturarELer = async () => {
    try {
      setCodigo("");
      setFormato("");

      if (!("BarcodeDetector" in window)) {
        showAlert("Este browser não suporta BarcodeDetector.", "warning");
        return;
      }

      const video = videoRef.current;

      if (!video || !video.videoWidth || !video.videoHeight) {
        showAlert("A imagem da câmara ainda não está disponível.", "warning");
        return;
      }

      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"],
      });

      const resultados = await detector.detect(video);

      if (!resultados || resultados.length === 0) {
        showAlert("Não foi detetado nenhum código nesta imagem.", "warning");
        return;
      }

      setCodigo(resultados[0].rawValue || "");
      setFormato(resultados[0].format || "");

      showAlert(`Código detetado: ${resultados[0].rawValue}`, "success");
    } catch (err) {
      console.error(err);
      showAlert("Erro ao tentar ler o código.", "error");
    }
  };

  useEffect(() => {
    setSuportaDetector("BarcodeDetector" in window);
    iniciarCamera();

    return () => {
      pararCamera();
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader title="Teste Barcode Detector" subtitle="Teste isolado e manual da câmara" />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3} display="grid" gap={2}>
            {loading && (
              <MDBox py={2} display="flex" justifyContent="center">
                <CircularProgress />
              </MDBox>
            )}

            <MDTypography variant="button" color={suportaDetector ? "success" : "error"}>
              BarcodeDetector: {suportaDetector ? "suportado" : "não suportado neste browser"}
            </MDTypography>

            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              style={{
                width: "100%",
                maxHeight: 420,
                background: "#000",
                borderRadius: 12,
                objectFit: "cover",
              }}
            />

            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton color="info" onClick={capturarELer}>
                Capturar e ler
              </MDButton>

              <MDButton color="secondary" onClick={iniciarCamera}>
                Reiniciar câmara
              </MDButton>
            </MDBox>

            <TextField
              label="Código detetado"
              value={codigo}
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <TextField label="Formato" value={formato} fullWidth InputProps={{ readOnly: true }} />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
